// services/dataService.js
/**
 * Service gérant toutes les opérations de données de l'application.
 * 
 * Ce service sert de point central pour toutes les manipulations de données,
 * qu'elles soient en ligne ou hors ligne. Il offre :
 * 
 * - Un stockage local robuste des données avec localforage
 * - Un cache en mémoire pour les données fréquemment accédées
 * - Une gestion transparente de la connectivité réseau
 * - Une interface unifiée pour accéder aux données, indépendamment de leur source
 * 
 * Ce service est conçu pour fonctionner de manière optimale même lorsque 
 * l'appareil est hors ligne. Les modifications sont d'abord enregistrées localement,
 * puis synchronisées avec le serveur lorsque la connexion est rétablie.
 */
import localforage from 'localforage';
import logService from '@/services/logService';

// Configuration du stockage principal
const DB_NAME = 'peppr-app-data';
const DB_VERSION = 1;

// Types d'entités gérées par le service
const ENTITY_TYPES = {
  DELIVERY: 'delivery',
  SHOP_DELIVERY: 'shop_delivery',
  TOURNEE: 'tournee',
  PENDING_UPDATE: 'pendingUpdate',
  PENDING_IMAGE: 'pendingImage'
};

// Création des instances de stockage
const stores = {
  // Données principales
  deliveries: localforage.createInstance({ name: `${DB_NAME}_deliveries` }),
  shopDeliveries: localforage.createInstance({ name: `${DB_NAME}_shop_deliveries` }),
  tournees: localforage.createInstance({ name: `${DB_NAME}_tournees` }),
  
  // Données en attente de synchronisation
  pendingUpdates: localforage.createInstance({ name: `${DB_NAME}_pendingUpdates` }),
  pendingImages: localforage.createInstance({ name: `${DB_NAME}_pendingImages` }),
};

// Cache mémoire pour les données fréquemment accédées
const memoryCache = {
  deliveries: new Map(),
  shopDeliveries: new Map(),
  tournees: new Map(),
};

// Configuration des stratégies de cache
const cacheConfig = {
  // Durée de validité du cache en millisecondes
  deliveryTTL: 24 * 60 * 60 * 1000, // 24 heures
  tourneeTTL: 12 * 60 * 60 * 1000,  // 12 heures
  
  // Taille maximale du cache mémoire (nombre d'éléments)
  maxMemoryCacheSize: 100,
  
  // Durée avant expiration pour les données "à rafraîchir"
  refreshThreshold: 5 * 60 * 1000 // 5 minutes
};

/**
 * Fonction utilitaire pour nettoyer un objet avant stockage
 * @param {Object} obj - L'objet à nettoyer
 * @returns {Object} - L'objet nettoyé
 */
function sanitizeObject(obj) {
  if (!obj) return obj;

  // Si ce n'est pas un objet ou un tableau, retourner tel quel
  if (typeof obj !== 'object') return obj;

  // Si c'est un tableau, mapper et sanitiser chaque élément
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item));
  }

  // Si c'est un objet Date, convertir en string ISO
  if (obj instanceof Date) {
    return obj.toISOString();
  }

  // Si c'est un objet, créer une copie propre
  const cleanObj = {};

  // Parcourir toutes les propriétés énumérables
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      // Ignorer les propriétés non-sérialisables
      if (typeof value === 'function' || value === undefined) {
        continue;
      }

      // Ignorer les propriétés commençant par un underscore (souvent des propriétés internes Vue)
      if (key.startsWith('_')) {
        continue;
      }

      // Traitement spécial pour __ob__ (Vue observable)
      if (key === '__ob__') {
        continue;
      }

      // Récursion pour nettoyer les sous-objets
      cleanObj[key] = sanitizeObject(value);
    }
  }

  return cleanObj;
}

/**
 * Normalise la structure d'un objet livraison pour garantir un format cohérent
 * @param {Object} delivery - Objet livraison à normaliser
 * @returns {Object} - Objet livraison normalisé
 */
function normalizeDelivery(delivery) {
  if (!delivery) return null;
  
  const normalized = { ...delivery };
  
  // Standardiser les propriétés qui peuvent varier
  if (delivery.undoneReason !== undefined && delivery.undone_reason === undefined) {
    normalized.undone_reason = delivery.undoneReason;
  } else if (delivery.undone_reason !== undefined && delivery.undoneReason === undefined) {
    normalized.undoneReason = delivery.undone_reason;
  }
  
  // S'assurer que l'ID est toujours une chaîne de caractères
  if (normalized.id !== undefined && typeof normalized.id !== 'string') {
    normalized.id = String(normalized.id);
  }
  
  return normalized;
}

/**
 * Détermine si une entrée de cache est expirée
 * @param {Object} cacheEntry - Entrée de cache avec timestamp
 * @param {number} ttl - Durée de vie en millisecondes
 * @returns {boolean} - True si l'entrée est expirée
 */
function isCacheExpired(cacheEntry, ttl) {
  if (!cacheEntry || !cacheEntry.timestamp) return true;
  
  const now = Date.now();
  const timestamp = typeof cacheEntry.timestamp === 'string' 
    ? new Date(cacheEntry.timestamp).getTime() 
    : cacheEntry.timestamp;
  
  return (now - timestamp) > ttl;
}

/**
 * Détermine si une entrée de cache doit être rafraîchie bien que non expirée
 * @param {Object} cacheEntry - Entrée de cache avec timestamp
 * @returns {boolean} - True si l'entrée doit être rafraîchie
 */
function shouldRefreshCache(cacheEntry) {
  if (!cacheEntry || !cacheEntry.timestamp) return true;
  
  const now = Date.now();
  const timestamp = typeof cacheEntry.timestamp === 'string'
    ? new Date(cacheEntry.timestamp).getTime()
    : cacheEntry.timestamp;
  
  return (now - timestamp) > cacheConfig.refreshThreshold;
}

/**
 * Crée un objet événement pour notifier un changement de données
 * @param {string} type - Type d'événement (update, create, delete)
 * @param {string} entityType - Type d'entité (delivery, tournee)
 * @param {string} entityId - ID de l'entité
 * @param {Object} data - Données associées à l'événement
 * @returns {CustomEvent} - Événement personnalisé
 */
function createDataEvent(type, entityType, entityId, data) {
  return new CustomEvent('data-change', {
    detail: {
      type,
      entityType,
      entityId,
      data,
      timestamp: Date.now()
    }
  });
}

/**
 * Classe principale pour la gestion des données
 */
class DataService {
  constructor() {
    this.initialized = false;
    this.lastRefreshTime = null;
    this.refreshPromises = new Map();
    
    // Initialiser les stores en tant que propriétés de la classe
    this.deliveriesStore = localforage.createInstance({
      name: 'appDelivery',
      storeName: 'deliveries'
    });
    
    this.shopDeliveriesStore = stores.shopDeliveries;
    this.tourneesStore = localforage.createInstance({
      name: 'appDelivery',
      storeName: 'tournees'
    });
    
    this.pendingUpdatesStore = localforage.createInstance({
      name: 'appDelivery',
      storeName: 'pendingUpdates'
    });
    
    this.pendingImagesStore = localforage.createInstance({
      name: 'appDelivery',
      storeName: 'pendingImages'
    });
    
    // Store pour les objets File/Blob des images
    this.imageObjectsStore = null; // Sera initialisé à la demande
    
    // Nouveau: Cache en mémoire pour les objets File récupérés
    this.fileObjectsCache = new Map();
    // Taille maximum du cache mémoire pour les objets File (nombre d'éléments)
    this.maxFileObjectsCacheSize = 50;
    // Durée de vie des éléments du cache (en millisecondes) - 24 heures
    this.fileObjectsCacheTTL = 1440 * 60 * 1000;
  }
  
  /**
   * Initialise le service de données
   * @returns {Promise<void>}
   */
  async init() {
    try {
      // Vider le cache mémoire des objets File au démarrage
      if (this.fileObjectsCache) {
        this.clearFileObjectsCache();
      }
      
      // Initialiser les stores si nécessaire
      await Promise.all([
        this.deliveriesStore.ready(),
        this.tourneesStore.ready(),
        this.pendingUpdatesStore.ready(),
        this.pendingImagesStore.ready()
      ]);
      
      // Initialiser l'état de santé du stockage
      await this.checkStorageHealth();
      
      // Vérifier le dernier rafraîchissement
      const lastRefreshStr = localStorage.getItem('lastDataRefresh');
      if (lastRefreshStr) {
        this.lastRefreshTime = parseInt(lastRefreshStr, 10);
      }
      
      console.log('Service de données initialisé avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du service de données:', error);
      throw error;
    }
  }
  
  /**
   * Vérifie l'état de santé du stockage local
   */
  async checkStorageHealth() {
    try {
      const stats = {
        deliveries: 0,
        tournees: 0,
        pendingUpdates: 0,
        pendingImages: 0,
        totalSize: 0
      };
      
      // Vérifier chaque store
      for (const [storeName, store] of Object.entries(stores)) {
        const keys = await store.keys();
        stats[storeName] = keys.length;
        
        // Vérifier quelques clés aléatoires pour s'assurer que les données sont accessibles
        if (keys.length > 0) {
          const sampleKey = keys[Math.floor(Math.random() * keys.length)];
          const item = await store.getItem(sampleKey);
          
          if (!item) {
            logService.warn(`Données potentiellement corrompues dans ${storeName} pour la clé ${sampleKey}`);
          }
        }
      }
      
      // Estimer la taille totale du stockage (approximatif)
      stats.totalSize = await this._estimateStorageSize();
      
      logService.info('Vérification du stockage terminée', { stats });
      
      return stats;
    } catch (error) {
      logService.error('Erreur lors de la vérification du stockage', { error: error.message });
      return null;
    }
  }
  
  /**
   * Estime la taille du stockage local (approximatif)
   */
  async _estimateStorageSize() {
    try {
      // Utiliser navigator.storage si disponible (navigateurs modernes)
      if (navigator.storage && navigator.storage.estimate) {
        const estimate = await navigator.storage.estimate();
        return estimate.usage || 0;
      }
      
      // Sinon, estimation approximative basée sur un échantillon
      let totalSize = 0;
      const SAMPLE_SIZE = 5;
      
      for (const store of Object.values(stores)) {
        const keys = await store.keys();
        
        // Prendre jusqu'à SAMPLE_SIZE clés aléatoires
        const sampleKeys = keys.length <= SAMPLE_SIZE 
          ? keys 
          : Array.from({ length: SAMPLE_SIZE }, () => keys[Math.floor(Math.random() * keys.length)]);
        
        // Récupérer les données et estimer la taille
        let sampleSize = 0;
        for (const key of sampleKeys) {
          const item = await store.getItem(key);
          if (item) {
            const itemStr = JSON.stringify(item);
            sampleSize += itemStr.length * 2; // Approximation en bytes
          }
        }
        
        // Extrapoler pour estimer la taille totale
        if (sampleKeys.length > 0) {
          const avgSize = sampleSize / sampleKeys.length;
          totalSize += avgSize * keys.length;
        }
      }
      
      return Math.round(totalSize);
    } catch (error) {
      logService.error('Erreur lors de l\'estimation de la taille du stockage', { error: error.message });
      return 0;
    }
  }
  
  /**
   * Récupère une livraison par son ID
   * @param {string} deliveryId - ID de la livraison
   * @param {Object} options - Options de récupération
   * @returns {Promise<Object>} - La livraison
   */
  async getDelivery(deliveryId, options = {}) {
    if (!deliveryId) throw new Error('ID de livraison requis');
    
    const { 
      bypassCache = false, 
      forceRefresh = false 
    } = options;
    
    // Vérifier le cache mémoire d'abord si pas de bypass
    if (!bypassCache) {
      const memoryCacheEntry = memoryCache.deliveries.get(deliveryId);
      if (memoryCacheEntry && !isCacheExpired(memoryCacheEntry, cacheConfig.deliveryTTL) && !forceRefresh) {
        // Si le cache n'est pas expiré mais qu'il faudrait le rafraîchir bientôt,
        // déclencher un rafraîchissement en arrière-plan
        if (shouldRefreshCache(memoryCacheEntry)) {
          this._triggerBackgroundRefresh(ENTITY_TYPES.DELIVERY, deliveryId);
        }
        return memoryCacheEntry.data;
      }
    }
    
    // Ensuite essayer depuis le stockage local
    try {
      const deliveryKey = `delivery_${deliveryId}`;
      const storedDelivery = await this.deliveriesStore.getItem(deliveryKey);
      
      if (storedDelivery && !isCacheExpired(storedDelivery, cacheConfig.deliveryTTL) && !forceRefresh) {
        // Mettre à jour le cache mémoire avec cette valeur
        this._updateMemoryCache(ENTITY_TYPES.DELIVERY, deliveryId, storedDelivery.data);
        
        // Comme pour le cache mémoire, déclencher un rafraîchissement en arrière-plan si nécessaire
        if (shouldRefreshCache(storedDelivery)) {
          this._triggerBackgroundRefresh(ENTITY_TYPES.DELIVERY, deliveryId);
        }
        
        return storedDelivery.data;
      }
      
      // Si on arrive ici, soit le cache a expiré, soit on a demandé un forceRefresh
      // La synchronisation avec le serveur n'est pas gérée ici, mais par le syncService
      return storedDelivery ? storedDelivery.data : null;
    } catch (error) {
      logService.error('Erreur lors de la récupération de la livraison', { 
        deliveryId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Récupère une livraison shop par son ID
   * @param {string} deliveryId - ID de la livraison shop
   * @param {Object} options - Options de récupération
   * @returns {Promise<Object>} - La livraison shop
   */
  async getShopDelivery(deliveryId, options = {}) {
    if (!deliveryId) throw new Error('ID de livraison shop requis');
    
    const { 
      bypassCache = false, 
      forceRefresh = false 
    } = options;
    
    // Vérifier le cache mémoire d'abord si pas de bypass
    if (!bypassCache) {
      const memoryCacheEntry = memoryCache.shopDeliveries.get(deliveryId);
      if (memoryCacheEntry && !isCacheExpired(memoryCacheEntry, cacheConfig.deliveryTTL) && !forceRefresh) {
        // Si le cache n'est pas expiré mais qu'il faudrait le rafraîchir bientôt,
        // déclencher un rafraîchissement en arrière-plan
        if (shouldRefreshCache(memoryCacheEntry)) {
          this._triggerBackgroundRefresh(ENTITY_TYPES.SHOP_DELIVERY, deliveryId);
        }
        return memoryCacheEntry.data;
      }
    }
    
    // Ensuite essayer depuis le stockage local
    try {
      const deliveryKey = `shop_delivery_${deliveryId}`;
      const storedDelivery = await this.shopDeliveriesStore.getItem(deliveryKey);
      
      if (storedDelivery && !isCacheExpired(storedDelivery, cacheConfig.deliveryTTL) && !forceRefresh) {
        // Mettre à jour le cache mémoire avec cette valeur
        this._updateMemoryCache(ENTITY_TYPES.SHOP_DELIVERY, deliveryId, storedDelivery.data);
        
        // Comme pour le cache mémoire, déclencher un rafraîchissement en arrière-plan si nécessaire
        if (shouldRefreshCache(storedDelivery)) {
          this._triggerBackgroundRefresh(ENTITY_TYPES.SHOP_DELIVERY, deliveryId);
        }
        
        return storedDelivery.data;
      }
      
      // Si on arrive ici, soit le cache a expiré, soit on a demandé un forceRefresh
      // La synchronisation avec le serveur n'est pas gérée ici, mais par le syncService
      return storedDelivery ? storedDelivery.data : null;
    } catch (error) {
      logService.error('Erreur lors de la récupération de la livraison shop', { 
        deliveryId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Déclenche un rafraîchissement en arrière-plan pour une entité
   * @param {string} entityType - Type d'entité (ENTITY_TYPES)
   * @param {string} entityId - ID de l'entité
   * @private
   */
  _triggerBackgroundRefresh(entityType, entityId) {
    // Éviter les rafraîchissements simultanés pour la même entité
    const refreshKey = `${entityType}_${entityId}`;
    if (this.refreshPromises.has(refreshKey)) return;
    
    // Pour le moment, ce mécanisme est un espace réservé pour une future implémentation
    // qui pourrait faire une requête réseau pour actualiser les données
    
    // Marquer le début du rafraîchissement
    this.refreshPromises.set(refreshKey, true);
    
    // Nettoyer la référence après un certain délai (pour éviter les fuites de mémoire)
    setTimeout(() => {
      this.refreshPromises.delete(refreshKey);
    }, 30000); // 30 secondes maximum
    
    // En implémentation réelle, on émettrait un événement pour demander un rafraîchissement
    // et on attendrait une réponse avant de supprimer la référence du Map
  }
  
  /**
   * Stocke ou met à jour une livraison
   * @param {string} deliveryId - ID de la livraison
   * @param {Object} deliveryData - Données de la livraison
   * @returns {Promise<Object>} - La livraison mise à jour
   */
  async saveDelivery(deliveryId, deliveryData) {
    if (!deliveryId) throw new Error('ID de livraison requis');
    if (!deliveryData) throw new Error('Données de livraison requises');
    
    try {
      // Normaliser la structure
      const normalizedDelivery = normalizeDelivery(deliveryData);
      
      // Nettoyer l'objet pour le stockage
      const sanitizedDelivery = sanitizeObject(normalizedDelivery);
      
      // Créer une entrée de cache avec timestamp
      const cacheEntry = {
        data: sanitizedDelivery,
        timestamp: Date.now(),
        expiresAt: Date.now() + cacheConfig.deliveryTTL
      };
      
      // Stocker dans localforage
      const deliveryKey = `delivery_${deliveryId}`;
      await this.deliveriesStore.setItem(deliveryKey, cacheEntry);
      
      // Mettre à jour le cache mémoire
      this._updateMemoryCache(ENTITY_TYPES.DELIVERY, deliveryId, sanitizedDelivery);
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('update', ENTITY_TYPES.DELIVERY, deliveryId, sanitizedDelivery);
      window.dispatchEvent(dataEvent);
      
      return sanitizedDelivery;
    } catch (error) {
      logService.error('Erreur lors de l\'enregistrement de la livraison', { 
        deliveryId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Stocke ou met à jour une livraison shop
   * @param {string} deliveryId - ID de la livraison shop
   * @param {Object} deliveryData - Données de la livraison shop
   * @returns {Promise<Object>} - La livraison shop mise à jour
   */
  async saveShopDelivery(deliveryId, deliveryData) {
    if (!deliveryId) throw new Error('ID de livraison shop requis');
    if (!deliveryData) throw new Error('Données de livraison shop requises');
    
    try {
      // Nettoyer l'objet pour le stockage
      const sanitizedDelivery = sanitizeObject(deliveryData);
      
      // Créer une entrée de cache avec timestamp
      const cacheEntry = {
        data: sanitizedDelivery,
        timestamp: Date.now(),
        expiresAt: Date.now() + cacheConfig.deliveryTTL
      };
      
      // Stocker dans localforage
      const deliveryKey = `shop_delivery_${deliveryId}`;
      await this.shopDeliveriesStore.setItem(deliveryKey, cacheEntry);
      
      // Mettre à jour le cache mémoire
      this._updateMemoryCache(ENTITY_TYPES.SHOP_DELIVERY, deliveryId, sanitizedDelivery);
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('update', ENTITY_TYPES.SHOP_DELIVERY, deliveryId, sanitizedDelivery);
      window.dispatchEvent(dataEvent);
      
      return sanitizedDelivery;
    } catch (error) {
      logService.error('Erreur lors de l\'enregistrement de la livraison shop', { 
        deliveryId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Récupère une tournée par son ID
   * @param {string} tourneeId - ID de la tournée, ou 'current' pour la tournée actuelle
   * @param {Object} options - Options de récupération
   * @returns {Promise<Object>} - La tournée
   */
  async getTournee(tourneeId = 'current', options = {}) {
    const { 
      bypassCache = false, 
      forceRefresh = false 
    } = options;
    
    // Vérifier le cache mémoire d'abord si pas de bypass
    if (!bypassCache) {
      const memoryCacheEntry = memoryCache.tournees.get(tourneeId);
      if (memoryCacheEntry && !isCacheExpired(memoryCacheEntry, cacheConfig.tourneeTTL) && !forceRefresh) {
        // Si le cache n'est pas expiré mais qu'il faudrait le rafraîchir bientôt,
        // déclencher un rafraîchissement en arrière-plan
        if (shouldRefreshCache(memoryCacheEntry)) {
          this._triggerBackgroundRefresh(ENTITY_TYPES.TOURNEE, tourneeId);
        }
        return memoryCacheEntry.data;
      }
    }
    
    // Ensuite essayer depuis le stockage local
    try {
      const tourneeKey = tourneeId === 'current' ? 'current_tournee' : `tournee_${tourneeId}`;
      const storedTournee = await this.tourneesStore.getItem(tourneeKey);
      
      if (storedTournee && !isCacheExpired(storedTournee, cacheConfig.tourneeTTL) && !forceRefresh) {
        // Mettre à jour le cache mémoire avec cette valeur
        this._updateMemoryCache(ENTITY_TYPES.TOURNEE, tourneeId, storedTournee.data);
        
        // Comme pour le cache mémoire, déclencher un rafraîchissement en arrière-plan si nécessaire
        if (shouldRefreshCache(storedTournee)) {
          this._triggerBackgroundRefresh(ENTITY_TYPES.TOURNEE, tourneeId);
        }
        
        return storedTournee.data;
      }
      
      // Si on arrive ici, soit le cache a expiré, soit on a demandé un forceRefresh
      return storedTournee ? storedTournee.data : null;
    } catch (error) {
      logService.error('Erreur lors de la récupération de la tournée', { 
        tourneeId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Stocke ou met à jour une tournée
   * @param {string} tourneeId - ID de la tournée, ou 'current' pour la tournée actuelle
   * @param {Object} tourneeData - Données de la tournée
   * @returns {Promise<Object>} - La tournée mise à jour
   */
  async saveTournee(tourneeId = 'current', tourneeData) {
    if (!tourneeData) throw new Error('Données de tournée requises');
    
    try {
      // Nettoyer l'objet pour le stockage
      const sanitizedTournee = sanitizeObject(tourneeData);
      
      // Créer une entrée de cache avec timestamp
      const cacheEntry = {
        data: sanitizedTournee,
        timestamp: Date.now(),
        expiresAt: Date.now() + cacheConfig.tourneeTTL
      };
      
      // Stocker dans localforage
      const tourneeKey = tourneeId === 'current' ? 'current_tournee' : `tournee_${tourneeId}`;
      await this.tourneesStore.setItem(tourneeKey, cacheEntry);
      
      // Mettre à jour le cache mémoire
      this._updateMemoryCache(ENTITY_TYPES.TOURNEE, tourneeId, sanitizedTournee);
      
      // Mettre à jour également le cache pour toutes les livraisons de la tournée
      if (sanitizedTournee.deliveries && Array.isArray(sanitizedTournee.deliveries)) {
        for (const delivery of sanitizedTournee.deliveries) {
          if (delivery.id) {
            // Utiliser saveDelivery pour maintenir la cohérence des callbacks et timestamps
            await this.saveDelivery(delivery.id, delivery);
          }
        }
      }
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('update', ENTITY_TYPES.TOURNEE, tourneeId, sanitizedTournee);
      window.dispatchEvent(dataEvent);
      
      // Mettre à jour le timestamp de dernier rafraîchissement
      this.lastRefreshTime = Date.now();
      
      return sanitizedTournee;
    } catch (error) {
      logService.error('Erreur lors de l\'enregistrement de la tournée', { 
        tourneeId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Met à jour une livraison dans une tournée
   * @param {string} deliveryId - ID de la livraison à mettre à jour
   * @param {Object} updateData - Données de mise à jour
   * @param {string} tourneeId - ID de la tournée, ou 'current' pour la tournée actuelle
   * @returns {Promise<boolean>} - True si la mise à jour a réussi
   */
  async updateDeliveryInTournee(deliveryId, updateData, tourneeId = 'current') {
    if (!deliveryId) throw new Error('ID de livraison requis');
    if (!updateData) throw new Error('Données de mise à jour requises');
    
    try {
      // Récupérer la tournée actuelle
      const tournee = await this.getTournee(tourneeId);
      if (!tournee || !tournee.deliveries) {
        logService.warn(`Tournée ${tourneeId} non trouvée ou sans livraisons`);
        return false;
      }
      
      // Trouver et mettre à jour la livraison dans la tournée
      let updated = false;
      let updatedDelivery = null;
      
      const updatedDeliveries = tournee.deliveries.map(delivery => {
        if (delivery.id === deliveryId) {
          updated = true;
          // Normaliser la structure des données
          const normalizedUpdate = normalizeDelivery(updateData);
          
          // Fusionner les données existantes avec les mises à jour
          updatedDelivery = { ...delivery, ...normalizedUpdate };
          return updatedDelivery;
        }
        return delivery;
      });
      
      if (!updated) {
        logService.warn(`Livraison ${deliveryId} non trouvée dans la tournée ${tourneeId}`);
        return false;
      }
      
      // Mettre à jour la tournée avec les livraisons modifiées
      const updatedTournee = { ...tournee, deliveries: updatedDeliveries };
      await this.saveTournee(tourneeId, updatedTournee);
      
      // Mettre à jour également le cache de la livraison individuelle
      if (updatedDelivery) {
        await this.saveDelivery(deliveryId, updatedDelivery);
      }
      
      // Émettre un événement pour notifier les autres composants
      // (déjà fait par saveDelivery et saveTournee)
      
      return true;
    } catch (error) {
      logService.error('Erreur lors de la mise à jour de la livraison dans la tournée', { 
        deliveryId, 
        tourneeId,
        error: error.message 
      });
      return false;
    }
  }
  
  /**
   * Récupérer toutes les mises à jour en attente
   * @returns {Promise<Array>} - Liste des mises à jour en attente
   */
  async getPendingUpdates() {
    try {
      const keys = await this.pendingUpdatesStore.keys();
      const updates = [];
      
      for (const key of keys) {
        const update = await this.pendingUpdatesStore.getItem(key);
        if (update) {
          updates.push(update);
        }
      }
      
      return updates;
    } catch (error) {
      logService.error('Erreur lors de la récupération des mises à jour en attente', { 
        error: error.message 
      });
      return [];
    }
  }
  
  /**
   * Récupérer toutes les images en attente
   * @returns {Promise<Array>} - Liste des images en attente
   */
  async getPendingImages() {
    try {
      const keys = await this.pendingImagesStore.keys();
      const images = [];
      
      for (const key of keys) {
        const image = await this.getPendingImage(key);
        if (image) {
          images.push(image);
        }
      }
      
      return images;
    } catch (error) {
      logService.error('Erreur lors de la récupération des images en attente', { 
        error: error.message 
      });
      return [];
    }
  }
  
  /**
   * Stocker une mise à jour en attente
   * @param {Object} updateData - Données de la mise à jour
   * @returns {Promise<Object>} - La mise à jour stockée
   */
  async storePendingUpdate(updateData) {
    if (!updateData || !updateData.id) throw new Error('Données de mise à jour invalides');
    
    try {
      // Préparer la mise à jour pour le stockage
      const pendingUpdate = {
        ...updateData,
        timestamp: updateData.timestamp || new Date().toISOString()
      };
      
      // Stocker dans localforage
      await this.pendingUpdatesStore.setItem(pendingUpdate.id, pendingUpdate);
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('create', ENTITY_TYPES.PENDING_UPDATE, pendingUpdate.id, pendingUpdate);
      window.dispatchEvent(dataEvent);
      
      return pendingUpdate;
    } catch (error) {
      logService.error('Erreur lors du stockage de la mise à jour en attente', { 
        updateId: updateData.id, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Stocke une image en attente de synchronisation
   * @param {Blob|File|String} file - Fichier image ou données d'image (peut être un Blob, File, base64, etc.)
   * @param {Object} metadata - Métadonnées de l'image
   * @returns {Promise<string>} - ID de l'image stockée
   */
  async storePendingImage(file, metadata) {
    if (!file) throw new Error('Fichier image requis');
    if (!metadata || !metadata.deliveryId) throw new Error('Métadonnées requises avec deliveryId');
    
    try {
      // Générer un ID unique pour l'image
      const imageId = metadata.id || `img_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
      
      // S'assurer que file est bien un Blob ou File avant de continuer
      let processedFile = file;
      
      // Log pour diagnostiquer le type exact de file
      console.log(`Type de fichier reçu par storePendingImage: ${typeof file}, est Blob: ${file instanceof Blob}, est File: ${file instanceof File}`, {
        isString: typeof file === 'string',
        isBase64: typeof file === 'string' && file.startsWith('data:'),
        deliveryId: metadata.deliveryId,
        imageId
      });
      
      // Convertir les différents formats en Blob si nécessaire
      if (!(file instanceof Blob) && !(file instanceof File)) {
        // Si c'est une chaîne base64, la convertir en Blob
        if (typeof file === 'string' && file.startsWith('data:')) {
          // C'est une chaîne base64, convertir en Blob
          try {
            const response = await fetch(file);
            const blob = await response.blob();
            processedFile = new File([blob], `image_${Date.now()}.jpg`, {
              type: blob.type || 'image/jpeg',
              lastModified: Date.now()
            });
            console.log(`Base64 converti en Blob pour l'image ${imageId}`);
          } catch (convError) {
            console.error('Erreur lors de la conversion de base64 en Blob:', convError);
            throw new Error('Impossible de convertir la chaîne base64 en Blob');
          }
        } else if (typeof file === 'string') {
          // Vérifier si cette chaîne est un ID d'image existant
          if (file.startsWith('img_') || file.startsWith('shop_img_')) {
            console.log(`ID d'image détecté: ${file} - Tentative de réutilisation d'image existante`);
            
            try {
              // Essayer de récupérer l'image originale
              const originalImage = await this.pendingImagesStore.getItem(file);
              
              if (originalImage) {
                console.log(`Image originale trouvée pour l'ID ${file}, réutilisation des données existantes`);
                // Retourner simplement l'ID de l'image existante
                return file;
              }
            } catch (retrieveError) {
              console.warn(`Impossible de récupérer l'image originale pour ${file}:`, retrieveError);
              // Continuer avec le traitement normal comme chaîne
            }
          }
          
          // C'est une chaîne mais pas un base64, peut-être un chemin ou autre chose
          // Stocker directement comme chaîne, sans utiliser FileReader
          const imageData = {
            id: imageId,
            deliveryId: metadata.deliveryId,
            deliveryContext: metadata.deliveryContext || 'unknown',
            type: metadata.type || 'delivery',
            fileName: `image_${Date.now()}.jpg`,
            fileType: 'image/jpeg',
            file: file, // Stocker la chaîne directement
            isStringPath: true, // Marquer qu'il s'agit d'une chaîne
            timestamp: new Date().toISOString(),
            synced: false,
            syncAttempts: 0
          };
          
          // Stocker dans localforage
          await this.pendingImagesStore.setItem(imageId, imageData);
          
          // Émettre un événement pour notifier les autres composants
          const eventData = { ...imageData };
          delete eventData.file; // Ne pas inclure le contenu dans l'événement
          
          const dataEvent = createDataEvent('create', ENTITY_TYPES.PENDING_IMAGE, imageId, eventData);
          window.dispatchEvent(dataEvent);
          
          console.log(`Image ${imageId} stockée sous forme de chaîne (type: ${imageData.type}, contexte: ${imageData.deliveryContext})`);
          
          return imageId;
        } else {
          // Ni Blob, ni File, ni chaîne - Impossible de continuer
          console.error(`Type de fichier non pris en charge pour l'image ${imageId}:`, typeof file);
          throw new Error(`Type de fichier non pris en charge: ${typeof file}`);
        }
      }
      
      // À ce stade, processedFile devrait être un Blob ou File
      if (!(processedFile instanceof Blob) && !(processedFile instanceof File)) {
        console.error(`Échec de la conversion en Blob pour l'image ${imageId}`);
        throw new Error('Impossible de convertir le fichier en Blob');
      }
      
      // Convertir le fichier en ArrayBuffer pour le stockage, en utilisant FileReader
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (e) => reject(new Error(`Erreur lors de la lecture du fichier: ${e.target.error?.message || 'inconnu'}`));
        reader.readAsArrayBuffer(processedFile);
      });
      
      // Préparer les données de l'image
      const imageData = {
        id: imageId,
        deliveryId: metadata.deliveryId,
        deliveryContext: metadata.deliveryContext || 'unknown',
        type: metadata.type || 'delivery', // Pour identifier explicitement les shop_delivery
        fileName: processedFile.name || `image_${Date.now()}.jpg`,
        fileType: processedFile.type || 'image/jpeg',
        size: processedFile.size,
        file: arrayBuffer,
        timestamp: new Date().toISOString(),
        synced: false,
        syncAttempts: 0
      };
      
      // Stocker le File original dans un cache séparé pour un accès plus facile
      try {
        // Initialiser le store des objets image s'il n'existe pas déjà
        if (!this.imageObjectsStore) {
          this.imageObjectsStore = localforage.createInstance({
            name: 'images',
            storeName: 'image_objects'
          });
        }
        
        // Stocker l'objet File original pour utilisation ultérieure
        await this.imageObjectsStore.setItem(imageId, {
          id: imageId,
          fileObject: processedFile,
          timestamp: new Date().toISOString()
        });
        
        console.log(`Objet File original stocké dans le cache pour l'image ${imageId}`);
      } catch (cacheError) {
        console.warn(`Impossible de stocker l'objet File original dans le cache pour l'image ${imageId}:`, cacheError);
        // Continuer malgré l'erreur
      }
      
      // Stocker dans localforage
      await this.pendingImagesStore.setItem(imageId, imageData);
      
      // Émettre un événement pour notifier les autres composants
      // (sans inclure les données binaires)
      const eventData = { ...imageData };
      delete eventData.file; // Ne pas inclure le contenu binaire dans l'événement
      
      const dataEvent = createDataEvent('create', ENTITY_TYPES.PENDING_IMAGE, imageId, eventData);
      window.dispatchEvent(dataEvent);
      
      console.log(`Image ${imageId} stockée avec succès (type: ${imageData.type}, contexte: ${imageData.deliveryContext})`);
      
      return imageId;
    } catch (error) {
      logService.error('Erreur lors du stockage de l\'image en attente', { 
        deliveryId: metadata.deliveryId, 
        error: error.message 
      });
      throw error;
    }
  }
  
  /**
   * Supprime une mise à jour en attente
   * @param {string} updateId - ID de la mise à jour
   * @returns {Promise<boolean>} - True si suppression réussie
   */
  async removePendingUpdate(updateId) {
    try {
      await this.pendingUpdatesStore.removeItem(updateId);
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('delete', ENTITY_TYPES.PENDING_UPDATE, updateId, null);
      window.dispatchEvent(dataEvent);
      
      return true;
    } catch (error) {
      logService.error('Erreur lors de la suppression de la mise à jour en attente', { 
        updateId, 
        error: error.message 
      });
      return false;
    }
  }
  
  /**
   * Vide le cache mémoire des objets File
   */
  clearFileObjectsCache() {
    this.fileObjectsCache.clear();
    console.log('Cache mémoire des objets File vidé');
  }
  
  /**
   * Supprime une image en attente
   * @param {string} imageId - ID de l'image
   * @returns {Promise<boolean>} - True si suppression réussie
   */
  async removePendingImage(imageId) {
    try {
      await this.pendingImagesStore.removeItem(imageId);
      
      // Supprimer aussi du cache mémoire si présent
      if (this.fileObjectsCache.has(imageId)) {
        this.fileObjectsCache.delete(imageId);
      }
      
      // Tenter de supprimer également de l'imageObjectsStore si initialisé
      if (this.imageObjectsStore) {
        try {
          await this.imageObjectsStore.removeItem(imageId);
        } catch (e) {
          // Ignorer les erreurs
        }
      }
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('delete', ENTITY_TYPES.PENDING_IMAGE, imageId, null);
      window.dispatchEvent(dataEvent);
      
      return true;
    } catch (error) {
      logService.error('Erreur lors de la suppression de l\'image en attente', { 
        imageId, 
        error: error.message 
      });
      return false;
    }
  }
  
  /**
   * Nettoie les données expirées du cache
   * @returns {Promise<Object>} - Statistiques de nettoyage
   */
  async cleanupCache() {
    try {
      const stats = {
        deliveries: 0,
        tournees: 0
      };
      
      // Nettoyer le cache des livraisons
      const deliveryKeys = await this.deliveriesStore.keys();
      for (const key of deliveryKeys) {
        const cacheEntry = await this.deliveriesStore.getItem(key);
        if (cacheEntry && isCacheExpired(cacheEntry, cacheConfig.deliveryTTL)) {
          await this.deliveriesStore.removeItem(key);
          stats.deliveries++;
          
          // Supprimer également du cache mémoire si présent
          const deliveryId = key.replace('delivery_', '');
          if (memoryCache.deliveries.has(deliveryId)) {
            memoryCache.deliveries.delete(deliveryId);
          }
        }
      }
      
      // Nettoyer le cache des tournées
      const tourneeKeys = await this.tourneesStore.keys();
      for (const key of tourneeKeys) {
        const cacheEntry = await this.tourneesStore.getItem(key);
        if (cacheEntry && isCacheExpired(cacheEntry, cacheConfig.tourneeTTL)) {
          await this.tourneesStore.removeItem(key);
          stats.tournees++;
          
          // Supprimer également du cache mémoire si présent
          const tourneeId = key.replace('tournee_', '').replace('current_tournee', 'current');
          if (memoryCache.tournees.has(tourneeId)) {
            memoryCache.tournees.delete(tourneeId);
          }
        }
      }
      
      return stats;
    } catch (error) {
      logService.error('Erreur lors du nettoyage du cache', { 
        error: error.message 
      });
      return { deliveries: 0, tournees: 0 };
    }
  }
  
  /**
   * Met à jour le cache mémoire pour une entité
   * @param {string} entityType - Type d'entité (ENTITY_TYPES)
   * @param {string} entityId - ID de l'entité
   * @param {Object} data - Données à stocker
   * @private
   */
  _updateMemoryCache(entityType, entityId, data) {
    if (!entityType || !entityId || !data) return;
    
    let cache;
    
    switch (entityType) {
      case ENTITY_TYPES.DELIVERY:
        cache = memoryCache.deliveries;
        break;
      case ENTITY_TYPES.TOURNEE:
        cache = memoryCache.tournees;
        break;
      default:
        return;
    }
    
    // Vérifier si le cache a atteint sa taille maximale
    if (cache.size >= cacheConfig.maxMemoryCacheSize) {
      // Supprimer l'élément le plus ancien (FIFO)
      const oldestKey = cache.keys().next().value;
      cache.delete(oldestKey);
    }
    
    // Stocker les nouvelles données avec un timestamp
    cache.set(entityId, {
      data,
      timestamp: Date.now()
    });
  }
  
  /**
   * Expose les stores pour compatibilité avec le code existant (temporairement)
   * @deprecated Utilisez les méthodes spécifiques à la place
   */
  getStores() {
    return stores;
  }
  
  /**
   * Met à jour une image en attente avec de nouvelles données
   * @param {string} imageId - ID de l'image
   * @param {Object} imageData - Nouvelles données de l'image
   * @returns {Promise<boolean>} - True si la mise à jour a réussi
   */
  async updatePendingImage(imageId, imageData) {
    if (!imageId || !imageData) return false;
    
    try {
      await this.pendingImagesStore.setItem(imageId, imageData);
      
      // Émettre un événement pour notifier les autres composants
      // (sans inclure les données binaires)
      const eventData = { ...imageData };
      delete eventData.file; // Ne pas inclure le contenu binaire dans l'événement
      
      const dataEvent = createDataEvent('update', ENTITY_TYPES.PENDING_IMAGE, imageId, eventData);
      window.dispatchEvent(dataEvent);
      
      return true;
    } catch (error) {
      logService.error('Erreur lors de la mise à jour de l\'image en attente', { 
        imageId, 
        error: error.message 
      });
      return false;
    }
  }
  
  /**
   * Nettoie le cache mémoire des objets File
   * @private
   */
  _cleanupFileObjectsCache() {
    // Si le cache est vide, ne rien faire
    if (this.fileObjectsCache.size === 0) return;
    
    // Nettoyer les entrées expirées
    const now = Date.now();
    const expiredKeys = [];
    
    this.fileObjectsCache.forEach((value, key) => {
      if (value._cachedAt && now - value._cachedAt > this.fileObjectsCacheTTL) {
        expiredKeys.push(key);
      }
    });
    
    // Supprimer les entrées expirées
    expiredKeys.forEach(key => this.fileObjectsCache.delete(key));
    
    // Si le cache est toujours trop grand, supprimer les entrées les plus anciennes
    if (this.fileObjectsCache.size > this.maxFileObjectsCacheSize) {
      // Trier les entrées par date de mise en cache (les plus anciennes d'abord)
      const sortedEntries = [...this.fileObjectsCache.entries()]
        .sort((a, b) => (a[1]._cachedAt || 0) - (b[1]._cachedAt || 0));
      
      // Nombre d'entrées à supprimer
      const deleteCount = this.fileObjectsCache.size - this.maxFileObjectsCacheSize;
      
      // Supprimer les entrées les plus anciennes
      for (let i = 0; i < deleteCount; i++) {
        this.fileObjectsCache.delete(sortedEntries[i][0]);
      }
    }
  }
  
  /**
   * Récupérer une image en attente
   * @param {string} imageId - ID de l'image
   * @returns {Promise<Object>} - Données de l'image
   */
  async getPendingImage(imageId) {
    if (!imageId) return null;
    
    try {
      // Vérifier si l'image est déjà en cache mémoire
      if (this.fileObjectsCache.has(imageId)) {
        // Log pour distinguer les récupérations depuis le cache mémoire
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Récupération rapide de l'image ${imageId} depuis le cache mémoire`);
        }
        return this.fileObjectsCache.get(imageId);
      }
      
      // Nettoyer le cache si nécessaire
      this._cleanupFileObjectsCache();
      
      // Récupérer l'image du stockage principal
      const image = await this.pendingImagesStore.getItem(imageId);
      
      if (!image) return null;
      
      // Si l'image est déjà synchronisée, pas besoin de récupérer le blob
      if (image.synced && image.serverImageId) {
        console.log(`Image ${imageId} déjà synchronisée (serverImageId: ${image.serverImageId}), pas de récupération du blob`);
        // Stocker dans le cache mémoire avec timestamp
        image._cachedAt = Date.now();
        this.fileObjectsCache.set(imageId, image);
        return image;
      }
      
      // Essayer de récupérer l'objet File depuis le cache séparé
      try {
        // Initialiser le store des objets image s'il n'existe pas déjà
        if (!this.imageObjectsStore) {
          this.imageObjectsStore = localforage.createInstance({
            name: 'images',
            storeName: 'image_objects'
          });
        }
        
        // Récupérer l'objet image avec le File/Blob
        const cachedImage = await this.imageObjectsStore.getItem(imageId);
        
        if (cachedImage && cachedImage.fileObject && 
            (cachedImage.fileObject instanceof Blob || cachedImage.fileObject instanceof File)) {
          // Si on a trouvé un cache avec un objet File/Blob valide, le remplacer dans l'image originale
          console.log(`Récupération de l'objet File pour l'image ${imageId} depuis le cache IndexedDB`);
          image.file = cachedImage.fileObject;
        } else if (image.file && typeof image.file !== 'string' && 
                  !(image.file instanceof Blob) && !(image.file instanceof File)) {
          // Si l'image a un fichier qui n'est ni une chaîne, ni un Blob/File, c'est probablement un ArrayBuffer
          // Convertir l'ArrayBuffer en File
          if (image.file instanceof ArrayBuffer || ArrayBuffer.isView(image.file)) {
            console.log(`Conversion de l'ArrayBuffer en File pour l'image ${imageId}`);
            const blob = new Blob([image.file], { type: image.fileType || 'image/jpeg' });
            image.file = new File([blob], image.fileName || `image_${Date.now()}.jpg`, {
              type: image.fileType || 'image/jpeg',
              lastModified: Date.now()
            });
          }
        }
      } catch (cacheError) {
        console.warn(`Impossible de récupérer l'objet File de l'image ${imageId} depuis le cache:`, cacheError);
        // Continuer avec l'image originale
      }
      
      // Stocker dans le cache mémoire pour éviter les appels répétés
      image._cachedAt = Date.now();
      this.fileObjectsCache.set(imageId, image);
      
      return image;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'image en attente:', error);
      throw error;
    }
  }
  
  /**
   * Récupère toutes les clés des tournées stockées
   * @returns {Promise<Array<string>>} - Liste des clés de tournées
   */
  async getTourneeKeys() {
    try {
      return await this.tourneesStore.keys();
    } catch (error) {
      logService.error('Erreur lors de la récupération des clés de tournées', { 
        error: error.message 
      });
      return [];
    }
  }
  
  /**
   * Récupère une tournée par sa clé de stockage brute
   * @param {string} key - Clé brute de stockage
   * @returns {Promise<Object|null>} - La tournée ou null si non trouvée
   */
  async getTourneeByKey(key) {
    if (!key) return null;
    
    try {
      return await this.tourneesStore.getItem(key);
    } catch (error) {
      logService.error('Erreur lors de la récupération de la tournée par clé', { 
        key, 
        error: error.message 
      });
      return null;
    }
  }
  
  /**
   * Supprime une tournée par sa clé de stockage brute
   * @param {string} key - Clé brute de stockage
   * @returns {Promise<boolean>} - True si suppression réussie
   */
  async removeTourneeByKey(key) {
    if (!key) return false;
    
    try {
      await this.tourneesStore.removeItem(key);
      
      // Nettoyer le cache mémoire correspondant
      const tourneeId = key === 'current_tournee' ? 'current' : key.replace('tournee_', '');
      if (memoryCache.tournees.has(tourneeId)) {
        memoryCache.tournees.delete(tourneeId);
      }
      
      // Émettre un événement pour notifier les autres composants
      const dataEvent = createDataEvent('delete', ENTITY_TYPES.TOURNEE, tourneeId, null);
      window.dispatchEvent(dataEvent);
      
      return true;
    } catch (error) {
      logService.error('Erreur lors de la suppression de la tournée', { 
        key, 
        error: error.message 
      });
      return false;
    }
  }
  
  /**
   * Fonction utilitaire pour s'assurer que les données sont disponibles après un rechargement hors ligne
   * @returns {Promise<boolean>} - True si les données sont disponibles
   */
  async ensureOfflineDataAvailability() {
    try {
      // Vérifier la disponibilité de la tournée
      const tournee = await this.getTournee('current');
      
      // Si nous n'avons pas de tournée en cache, nous ne pouvons pas fonctionner hors ligne
      if (!tournee) {
        logService.warn('Aucune tournée disponible en cache pour le mode hors ligne');
        return false;
      }
      
      // Logger les informations sur la tournée trouvée
      logService.info('Tournée trouvée en cache pour le mode hors ligne', {
        tourneeId: tournee.id,
        tourneeDate: tournee.tournee_date,
        deliveriesCount: tournee.deliveries?.length || 0
      });
      
      // S'assurer que toutes les livraisons sont également dans leur cache individuel
      if (tournee.deliveries && tournee.deliveries.length > 0) {
        for (const delivery of tournee.deliveries) {
          // Vérifier si la livraison est déjà en cache
          const cachedDelivery = await this.getDelivery(delivery.id, { bypassCache: true });
          
          // Si pas en cache, la sauvegarder
          if (!cachedDelivery) {
            await this.saveDelivery(delivery.id, delivery);
            logService.debug(`Livraison ${delivery.id} mise en cache pour le mode hors ligne`);
          }
        }
      }
      
      return true;
    } catch (error) {
      logService.error('Erreur lors de la vérification des données hors ligne', { error: error.message });
      return false;
    }
  }
  
  /**
   * Récupère le timestamp du dernier rafraîchissement des données
   * @returns {number|null} - Timestamp ou null si aucun rafraîchissement
   */
  getLastRefreshTime() {
    return this.lastRefreshTime;
  }
  
  /**
   * Exporte les statistiques du cache
   * @returns {Promise<Object>} - Statistiques du cache
   */
  async getCacheStats() {
    try {
      const stats = {
        deliveries: 0,
        tournees: 0,
        pendingUpdates: 0,
        pendingImages: 0,
        memoryCacheSize: {
          deliveries: memoryCache.deliveries.size,
          tournees: memoryCache.tournees.size
        },
        lastRefreshTime: this.lastRefreshTime
      };
      
      // Compter les éléments dans chaque store
      for (const [storeName, store] of Object.entries(stores)) {
        const keys = await store.keys();
        stats[storeName] = keys.length;
      }
      
      return stats;
    } catch (error) {
      logService.error('Erreur lors de la récupération des statistiques du cache', { error: error.message });
      return {};
    }
  }
  
  /**
   * Réinitialise les connexions à IndexedDB
   * Utile en cas de perte de connexion à la base de données
   */
  async resetIndexedDBConnection() {
    try {
      console.log("Tentative de réinitialisation des connexions IndexedDB...");
      
      // Fermer toutes les connexions existantes
      if (this.db && this.db.close) {
        this.db.close();
      }
      
      if (this.deliveryStore && this.deliveryStore.close) {
        this.deliveryStore.close();
      }
      
      if (this.pendingUpdatesStore && this.pendingUpdatesStore.close) {
        this.pendingUpdatesStore.close();
      }
      
      if (this.pendingImagesStore && this.pendingImagesStore.close) {
        this.pendingImagesStore.close();
      }
      
      // Réinitialiser les instances
      this.db = null;
      this.deliveryStore = null;
      this.pendingUpdatesStore = null;
      this.pendingImagesStore = null;
      
      // Recréer les instances avec les options de durabilité plus permissives pour iOS
      // Utiliser des options relaxed pour maximiser les chances de succès
      const storeOptions = {
        driver: [localforage.INDEXEDDB, localforage.WEBSQL, localforage.LOCALSTORAGE],
        name: 'appDelivery',
        version: 18.0,
        storeName: 'appDelivery',
        description: 'Données locales de livraison',
        size: 75 * 1024 * 1024 // 75MB
      };
      
      // Reinitialiser avec des options moins strictes
      this.deliveryStore = localforage.createInstance({
        ...storeOptions,
        storeName: 'livraisons'
      });
      
      this.tourneeStore = localforage.createInstance({
        ...storeOptions,
        storeName: 'tournees'
      });
      
      this.pendingUpdatesStore = localforage.createInstance({
        ...storeOptions,
        storeName: 'pendingUpdates'
      });
      
      this.pendingImagesStore = localforage.createInstance({
        ...storeOptions,
        storeName: 'pendingImages'
      });
      
      // Force la création des stores immédiatement
      await Promise.all([
        this.deliveryStore.ready(),
        this.tourneeStore.ready(),
        this.pendingUpdatesStore.ready(),
        this.pendingImagesStore.ready()
      ]);
      
      console.log("Réinitialisation des connexions IndexedDB terminée avec succès");
      return true;
    } catch (error) {
      console.error("Échec de la réinitialisation des connexions IndexedDB:", error);
      throw error;
    }
  }
  
  /**
   * Teste la connexion à IndexedDB en effectuant une opération simple
   * @returns {Promise<boolean>} true si la connexion fonctionne, sinon lance une erreur
   */
  async testConnection() {
    try {
      // Tester chaque store avec une opération simple (get d'une clé qui n'existe probablement pas)
      await this.deliveryStore.getItem('test_connection');
      await this.tourneeStore.getItem('test_connection');
      await this.pendingUpdatesStore.getItem('test_connection');
      await this.pendingImagesStore.getItem('test_connection');
      
      // Si on arrive ici, c'est que la connexion fonctionne
      return true;
    } catch (error) {
      console.error("Échec du test de connexion à IndexedDB:", error);
      throw error;
    }
  }
}

// Exporter une instance unique
const dataService = new DataService();

// Exposer la fonction sanitizeObject pour d'autres services
dataService.sanitizeObject = sanitizeObject;

export default dataService; 